import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Autoplay,
  EffectCreative,
  Navigation,
  Pagination
} from 'swiper/modules';
import { CLASS_TRACKING_GTM } from 'constants/index';
import { HightLightResponse } from 'matrix';
import Link from 'next/link';
import Image from 'next/image';
import { createPropsToTrackingMatrix } from 'components/Integration/createPropsToTrackingMatrix';
import * as styles from './Carousel.css';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

interface CarouselProps {
  id: string;
  slides: HightLightResponse[];
  handleChangeSlide: (index: number) => void;
  handleClickSlide?: (slide: HightLightResponse, index: number) => void;
  componentTemplate: string;
  prefixTrackingName: string;
  matrizTemplate: string;
  templateType: string;
}

function Carousel({
  slides,
  id,
  handleChangeSlide,
  handleClickSlide,
  componentTemplate,
  prefixTrackingName,
  matrizTemplate,
  templateType
}: CarouselProps) {
  const hasFewSlides = slides?.length <= 5;

  return (
    <Swiper
      className={styles.customHightLightSwiper}
      id={id}
      pagination={{
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 3
      }}
      navigation={slides.length > 0}
      autoplay={{
        delay: 6000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      }}
      grabCursor
      centeredSlides
      loop
      slidesPerView={1.1}
      effect="creative"
      creativeEffect={{
        prev: {
          origin: 'right',
          translate: ['-100%', 0, 0],
          scale: 0.9
        },
        next: {
          origin: 'left',
          translate: ['100%', 0, 0],
          scale: 0.9
        }
      }}
      modules={[EffectCreative, Navigation, Pagination, Autoplay]}
      spaceBetween={8}
      touchEventsTarget="container"
      preventClicks={false}
      preventClicksPropagation={false}
      breakpoints={{
        1500: {
          spaceBetween: -400,
          slidesPerView: 3,
          simulateTouch: false,
          creativeEffect: {
            limitProgress: hasFewSlides ? 1 : 3,
            prev: {
              translate: ['-26%', 0, -20],
              scale: 0.9
            },
            next: {
              translate: ['26%', 0, -20],
              scale: 0.9
            }
          }
        },
        1100: {
          spaceBetween: -400,
          slidesPerView: 3,
          simulateTouch: false,
          creativeEffect: {
            limitProgress: hasFewSlides ? 1 : 3,
            prev: {
              translate: ['-20%', 0, -20],
              scale: 0.9
            },
            next: {
              translate: ['20%', 0, -20],
              scale: 0.9
            }
          }
        },
        830: {
          spaceBetween: -300,
          slidesPerView: hasFewSlides ? 3 : 5,
          creativeEffect: {
            limitProgress: hasFewSlides ? 1 : 3,
            prev: {
              translate: ['-28%', 0, -20],
              scale: 0.9
            },
            next: {
              translate: ['28%', 0, -20],
              scale: 0.9
            }
          }
        }
      }}
      onSwiper={(swiper) => handleChangeSlide(swiper.realIndex)}
      onSlideChange={(swiper) => handleChangeSlide(swiper.realIndex)}
      noSwipingClass="card-content"
    >
      {slides?.map((slide, index) => {
        const { dataCreative, sectionPosition } = createPropsToTrackingMatrix(
          index,
          {
            trackingPrefix: prefixTrackingName,
            matrizTemplate,
            componentTemplate,
            cardType: templateType,
            templateType,
            payloadCard: slide,
            dataLocation: null
          }
        );

        return (
          <SwiperSlide
            key={slide?.event_id || slide?.featured_id}
            className={`${CLASS_TRACKING_GTM.EVENT_CARD} ${styles.highLightSlide}`}
            data-bannerid={slide.event_id}
            data-name={slide.title}
            data-creative={dataCreative}
            data-position={`${sectionPosition || ''}`}
          >
            <Link
              href={slide.link_page}
              prefetch={false}
              className={`${CLASS_TRACKING_GTM.EVENT_CARD} ${styles.cardLink}`}
              onClick={() => handleClickSlide?.(slide, index)}
            >
              <div className={styles.imageSlide}>
                <Image
                  src={slide.image_url}
                  alt={slide.title || 'Banner Sympla'}
                  fill
                  priority={index < 3}
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  sizes="(max-width: 768px) 90vw, (max-width: 1200px) 33vw, 860px"
                  style={{ objectFit: 'cover' }}
                />
                <div className={styles.overlay} />
              </div>
            </Link>
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
}

export default Carousel;
